// N-15: 通知先名簿（tenant-portal「通知先登録」）の型・定数・純関数。
//
// **管理者向け通知の受信者を 1 箇所で決める**ための名簿。それまでは受信者の真実源が
// 7 系統（`admin_notification_settings` / `admin_users` の LINE 全件 / role 絞り込み /
// `hot.notifyEmails` / env / GCP channel …）に分裂しており、「メールは届くのに LINE は無音」
// 「無効化した管理者にも届き続ける」が構造的に起きていた（issues/open/N-15）。
//
// 名簿は **通知を受ける人**の一覧であって、ログインアカウント（`admin_users`）ではない。
// アカウントを持たないスタッフも登録でき、LINE は顧客一覧（`users`）から選ぶ。
//
// ここに置くのは apps（portal のプレビュー）と functions（宛先解決）が**同じ判定**を使うため。
// 「画面は届くと言うのに実際は届かない」を作らない。
import { isValidEmailFormat } from './adminEmail.js';
/** 通知の種類。名簿の 1 人ごとに「この種類をメール / LINE で受けるか」を持つ。 */
export const ADMIN_NOTIFICATION_KINDS = ['booking', 'billing', 'hot', 'ops'];
/** 画面表示ラベル（真実源はここ 1 箇所 / apps で書き写さない）。 */
export const ADMIN_NOTIFICATION_KIND_LABELS = {
    booking: '予約通知',
    billing: '決済アラート',
    hot: 'HOT 案件',
    ops: '運用アラート',
};
/** 種類ごとの補足（画面のツールチップ / 一覧の説明に使う）。 */
export const ADMIN_NOTIFICATION_KIND_DESCRIPTIONS = {
    booking: '新規予約・キャンセルの管理者向け通知',
    billing: '決済失敗・契約終了などの請求まわりの通知',
    hot: '買い替え意向などの HOT 案件が起票されたときの担当者通知',
    ops: '監視アラート・バックアップ検証失敗などの運用通知',
};
export const ADMIN_NOTIFICATION_CHANNELS = ['email', 'line'];
export const NOTIFICATION_RECIPIENTS_COLLECTION = 'notification_recipients';
/** 1 テナントの上限（Firestore 1 doc に収める / 単独店舗の運用では 10 人前後）。 */
export const NOTIFICATION_RECIPIENTS_MAX = 50;
export const NOTIFICATION_RECIPIENT_NAME_MAX = 60;
export const NOTIFICATION_RECIPIENT_ID_MAX = 64;
export const NOTIFICATION_RECIPIENT_EMAIL_MAX = 254;
export const NOTIFICATION_RECIPIENT_LINE_USER_ID_MAX = 128;
/** 全種類 × 全チャネル OFF のフラグ（画面の新規行の初期値）。 */
export function emptyAdminNotificationKindFlags() {
    return {
        booking: { email: false, line: false },
        billing: { email: false, line: false },
        hot: { email: false, line: false },
        ops: { email: false, line: false },
    };
}
function isChannelFlags(v) {
    return (typeof v === 'object' &&
        v !== null &&
        typeof v.email === 'boolean' &&
        typeof v.line === 'boolean');
}
/**
 * 名簿の構造検証（純関数 / **画面と Callable が同じ関数を通す**）。
 * 戻り値は日本語のエラー一覧（空なら OK）。zod の形式検査とは別に、
 * 「メールも LINE も無い人」「id 重複」のような**意味の検査**をここに置く。
 */
export function validateNotificationRecipients(recipients) {
    const errors = [];
    if (!Array.isArray(recipients))
        return ['recipients が配列ではありません'];
    if (recipients.length > NOTIFICATION_RECIPIENTS_MAX) {
        errors.push(`通知先は最大 ${NOTIFICATION_RECIPIENTS_MAX} 人までです（${recipients.length} 人）`);
    }
    const seenIds = new Set();
    recipients.forEach((r, i) => {
        const at = `${i + 1} 人目`;
        const id = typeof r.id === 'string' ? r.id.trim() : '';
        if (!id || id.length > NOTIFICATION_RECIPIENT_ID_MAX) {
            errors.push(`${at}: id が不正です`);
        }
        else if (seenIds.has(id)) {
            errors.push(`${at}: id が重複しています（${id}）`);
        }
        else {
            seenIds.add(id);
        }
        const name = typeof r.name === 'string' ? r.name.trim() : '';
        if (!name)
            errors.push(`${at}: 名前を入力してください`);
        else if (name.length > NOTIFICATION_RECIPIENT_NAME_MAX) {
            errors.push(`${at}: 名前は ${NOTIFICATION_RECIPIENT_NAME_MAX} 文字までです`);
        }
        const email = typeof r.email === 'string' ? r.email.trim() : '';
        const lineUserId = typeof r.lineUserId === 'string' ? r.lineUserId.trim() : '';
        if (email && !isValidEmailFormat(email)) {
            errors.push(`${at}: メールアドレスの形式が不正です（${email}）`);
        }
        if (email.length > NOTIFICATION_RECIPIENT_EMAIL_MAX) {
            errors.push(`${at}: メールアドレスが長すぎます`);
        }
        if (lineUserId.length > NOTIFICATION_RECIPIENT_LINE_USER_ID_MAX) {
            errors.push(`${at}: LINE 宛先の id が不正です`);
        }
        if (!email && !lineUserId) {
            errors.push(`${at}（${name || id}）: メールか LINE のどちらかを入れてください`);
        }
        if (typeof r.enabled !== 'boolean')
            errors.push(`${at}: enabled が不正です`);
        const kinds = r.kinds;
        if (typeof kinds !== 'object' || kinds === null) {
            errors.push(`${at}: 受け取る通知の設定がありません`);
        }
        else {
            for (const kind of ADMIN_NOTIFICATION_KINDS) {
                if (!isChannelFlags(kinds[kind])) {
                    errors.push(`${at}: ${ADMIN_NOTIFICATION_KIND_LABELS[kind]} の設定が不正です`);
                }
            }
        }
    });
    return errors;
}
/**
 * ある種類の通知を「誰に・どのチャネルで」送るかを名簿から選ぶ（純関数）。
 *
 * - `enabled: false` の人は両チャネルとも外す
 * - メールは `kinds[kind].email` が ON かつ形式が妥当なものだけ
 * - LINE は `kinds[kind].line` が ON かつ `lineUserId` があるものだけ
 * - 名簿が無い（`null` / `undefined`）ときは**空**を返す。「名簿が無い = 従来経路へ倒す」の
 *   判断は呼出側（functions の `resolveNotificationRecipients`）が doc の有無で行う
 */
export function selectRecipientsForKind(doc, kind) {
    const emails = [];
    const seenEmail = new Set();
    const lineUserIds = [];
    const seenLine = new Set();
    for (const r of doc?.recipients ?? []) {
        if (!r || r.enabled !== true)
            continue;
        const flags = r.kinds?.[kind];
        if (!flags)
            continue;
        if (flags.email === true) {
            const email = typeof r.email === 'string' ? r.email.trim() : '';
            const key = email.toLowerCase();
            if (email && isValidEmailFormat(email) && !seenEmail.has(key)) {
                seenEmail.add(key);
                emails.push(email);
            }
        }
        if (flags.line === true) {
            const lineUserId = typeof r.lineUserId === 'string' ? r.lineUserId.trim() : '';
            if (lineUserId && !seenLine.has(lineUserId)) {
                seenLine.add(lineUserId);
                lineUserIds.push(lineUserId);
            }
        }
    }
    return { emails, lineUserIds };
}
//# sourceMappingURL=notificationRecipients.js.map